import { MuiAlert, strIsNullOrEmpty } from '@fox/atoms';
import { QueryFilter, SortState } from '@fox/data-grid';
import { useMediaQuery } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useContentContext } from '../../content-context';
import { useActionCentreColumns, useActionCentreSources } from '../query-hooks/query-implementations';
import { ActionCentreColumn, IActionCentreTableProps } from './contracts';
import { LeftSideFilterView } from './left-side-filter-view';
import { MobileView } from './mobile-view';
import { TopFilterView } from './top-filter-view';

export const ActionCentre: React.FC<IActionCentreTableProps> = ({
	actionId,
	rowsExpandedOnSmallScreens,
	disableExport,
	filterPositioning,
	rowsPerPage: rowsPersPage
}) => {
	const pageSize = rowsPersPage || 10; // Default to 10 if not provided
	const { contentId, requestToken } = useContentContext();
	const [sourceFilter, setSourceFilter] = useState<string | undefined>(undefined);
	const [filters, setFilters] = useState<QueryFilter[]>([]);
	const [sort, setSort] = useState<SortState | undefined>(undefined);
	const [page, setPage] = useState<number>(1);
	const isMobile = useMediaQuery('(max-width: 800px)');

	const { data: sources, error, loading } = useActionCentreSources({ contentId, requestToken, actionId, skip: !actionId });
	const {
		data: columnsResult,
		error: columnsError,
		loading: columnsLoading
	} = useActionCentreColumns({
		actionId,
		contentId,
		requestToken,
		skip: !actionId
	});

	const updateFilters = useCallback(
		(value: string | undefined, column?: ActionCentreColumn) => {
			const columnIdentifier: 'id' | 'title' = column.type === 'custom' ? 'id' : 'title'; //for some reason action centres use title for standard fields and dataindex for customfields :(
			let updatedFilters = [...filters];
			const existingIndex = updatedFilters.findIndex(f => f.dataIndex === column[columnIdentifier]);
			if (existingIndex > -1) {
				if (strIsNullOrEmpty(value)) {
					updatedFilters = updatedFilters?.filter(f => f.dataIndex !== column[columnIdentifier]);
				} else {
					updatedFilters[existingIndex].filterValue = value;
				}
			} else {
				if (strIsNullOrEmpty(value)) return;
				updatedFilters.push({
					dataIndex: column[columnIdentifier],
					filterValue: value,
					filterType: column.filterType
				});
			}
			setFilters(updatedFilters);
			setPage(1); //reset to page 1 when filters change
		},
		[filters]
	);

	if (!strIsNullOrEmpty(error || columnsError)) return <MuiAlert severity="error">{error || columnsError}</MuiAlert>;
	if (loading || columnsLoading || !columnsResult?.columns?.length) return null;
	const sourceOptions = sources ? Object.entries(sources) : [];
	const filter = sourceFilter ?? sourceOptions?.[0]?.[0];
	const showSearch = columnsResult.columns.some(c => c.filterType === 'string' && !!c.filterable);
	const showFilters = columnsResult.columns.some(c => c.filterable);
	const enableClearFilters = filters.length > 0;

	if (isMobile) {
		return (
			<MobileView
				actionId={actionId}
				columnsResult={columnsResult}
				disableExport={disableExport}
				enableClearFilters={enableClearFilters}
				filter={filter}
				filters={filters}
				page={page}
				pageSize={pageSize}
				setFilters={setFilters}
				setPage={setPage}
				setSourceFilter={setSourceFilter}
				showFilters={showFilters}
				sourceOptions={sourceOptions}
				sort={sort}
				updateFilters={updateFilters}
				setSort={setSort}
				showSearch={showSearch}
				rowsExpandedOnSmallScreens={rowsExpandedOnSmallScreens}
			/>
		);
	}
	if (filterPositioning === 'Left') {
		return (
			<LeftSideFilterView
				actionId={actionId}
				columnsResult={columnsResult}
				disableExport={disableExport}
				enableClearFilters={enableClearFilters}
				filter={filter}
				filters={filters}
				page={page}
				pageSize={pageSize}
				setFilters={setFilters}
				setPage={setPage}
				setSourceFilter={setSourceFilter}
				showFilters={showFilters}
				sourceOptions={sourceOptions}
				sort={sort}
				updateFilters={updateFilters}
				setSort={setSort}
				showSearch={showSearch}
				rowsExpandedOnSmallScreens={false}
			/>
		);
	}
	return (
		<TopFilterView
			actionId={actionId}
			columnsResult={columnsResult}
			disableExport={disableExport}
			enableClearFilters={enableClearFilters}
			filter={filter}
			filters={filters}
			page={page}
			pageSize={pageSize}
			setFilters={setFilters}
			setPage={setPage}
			setSourceFilter={setSourceFilter}
			showFilters={showFilters}
			sourceOptions={sourceOptions}
			sort={sort}
			updateFilters={updateFilters}
			setSort={setSort}
			rowsExpandedOnSmallScreens={false}
			showSearch={showSearch}
		/>
	);
};
